import { SpyneTrait } from "spyne";
import { PageCardView } from "components/cards/page-card-view";
import { PageCardExpandedView } from "components/cards/page-card-expanded-view";
import { compose, filter, head, path, propEq } from "ramda";
import { Page404View } from "components/pages/page-404-view";
import { gsap, Power2 } from "gsap";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";
export class PageTraits extends SpyneTrait {
  constructor(context) {
    let traitPrefix = "page$";
    gsap.registerPlugin(ScrollToPlugin); // (or in the top-level file in your React app)

    super(context, traitPrefix);
  }

  static page$GetContentLength(data = this.props.data) {
    const { content } = data;
    return (Array.isArray(content) && content.length) || 0;
  }

  static page$CustomizeById() {
    const { pageId } = this.props.data;
    const defaultFn = () => {};

    const customizePageFnHash = {
      addPageIdHere: defaultFn,
    };

    const fn = customizePageFnHash[pageId] || defaultFn;
    return fn();
  }

  static page$GetPageDataById(id) {
    return compose(
        head,
        filter(propEq(id, "pageId")),
        path(["Spyne", "config", "data", "content"]),
    )(window);
  }

  static page$InitPage() {
    const bgImg$ = this.props.el$(".bg-image");

    if (bgImg$.exists) {
      bgImg$.addAnimClass("reveal");
    }
  }

  static page$checkFor404() {
    const { cardId } = this.props.routeData;
    if (cardId === "404") {
      this.appendView(new Page404View());
    }
  }

  static page$ScrollToTopOnLoad(){
    const scrollToTop = ()=>window.scrollTo(0,0);
    requestAnimationFrame(scrollToTop);
  }

  static page$CheckToAddCards(props = this.props) {
    const { pageId } = props.data;
    const addCard = (data) => {
      data["pageId"] = pageId;
      this.appendView(new PageCardView({ data }), ".app-page-subnav-container");
    };
    if (props.data.content) {
      props.data.content.forEach(addCard);
    }
  }

  static page$CheckToAddCardPage(props = this.props) {
    const { cardExistsBool } = props;
    const data = props.data.cardData;
    if (cardExistsBool) {
      ``;
      this.appendView(new PageCardExpandedView({ data }));
    }
  }

  // Scrolls a docs deep-link target into view. Two realities of this page
  // make a single fire-and-forget scroll unreliable: (1) content items
  // render asynchronously (per-item rAF + block hydration), so the target
  // may not exist yet when the deep link fires — wait for it; (2) content
  // above the target keeps growing as it hydrates, so a landed scroll
  // drifts — keep correcting until the position holds. Every pass re-arms
  // the scroll-spy pause; everything cancels the moment the user scrolls
  // on their own.
  // opts.instant — no scroll animation: browser back/forward should land
  // directly on the target (queued tweens across multiple history jumps
  // get jumbled, and instant arrival reads as more coherent). Corrective
  // passes snap instead of tweening too.
  static page$ScrollDocsSelIntoView(docsSel, props = this.props, opts = {}) {
    const { instant = false } = opts;
    const offset = props?.scrollOffsetY || 0;
    const DRIFT_TOLERANCE = 24;
    const MAX_SCROLL_TRIES = 8;
    const MAX_WAIT_TRIES = 25;   // 25 × 400ms — outlasts slow hydration
    const WAIT_INTERVAL = 400;
    const CHECK_INTERVAL = 700;

    let scrollTries = 0;
    let waitTries = 0;
    let stableChecks = 0;
    let userTookOver = false;

    const onUserInput = () => {
      userTookOver = true;
    };
    // pointerdown covers scrollbar drags and touch — scrolling by
    // scrollbar emits no wheel event.
    ["wheel", "touchstart", "keydown", "pointerdown"].forEach((evt) =>
        window.addEventListener(evt, onUserInput, { passive: true, once: true }),
    );

    // While this loop is live the pause is re-armed with a quiet window
    // LONGER than the loop cadence — otherwise the spy resumes in the gap
    // between passes and publishes whatever intermediate section the
    // hydrating page happens to be on. The terminal tick doesn't re-arm,
    // so the final resume re-emits the settled target.
    const PAUSE_SPAN = 1000;

    const armPause = () => {
      if (typeof this.scrollSpy$PauseScrollSpy === "function") {
        this.scrollSpy$PauseScrollSpy(props, PAUSE_SPAN);
      }
    };

    const scrollToTarget = () => {
      armPause();

      if (instant === true) {
        const el = document.querySelector(docsSel);
        if (el !== null) {
          const y = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo(0, Math.max(0, y));
        }
        return;
      }

      gsap.to(window, {
        duration: .25,
        scrollTo: { y: docsSel, offsetY: offset },
      });
    };

    const check = () => {
      const el = document.querySelector(docsSel);
      if (
          el === null ||
          userTookOver === true ||
          scrollTries >= MAX_SCROLL_TRIES ||
          stableChecks >= 4
      ) {
        return;
      }
      const drift = Math.abs(el.getBoundingClientRect().top - offset);
      if (drift > DRIFT_TOLERANCE) {
        stableChecks = 0;
        scrollTries += 1;
        scrollToTarget();
      } else {
        stableChecks += 1;
        armPause();
      }
      window.setTimeout(check, CHECK_INTERVAL);
    };

    const waitForTarget = () => {
      if (userTookOver === true) {
        return;
      }
      if (document.querySelector(docsSel) === null) {
        waitTries += 1;
        if (waitTries < MAX_WAIT_TRIES) {
          // Hold the spy while waiting so it can't publish the top
          // section before the target has even rendered.
          armPause();
          window.setTimeout(waitForTarget, WAIT_INTERVAL);
        }
        return;
      }
      scrollToTarget();
      window.setTimeout(check, CHECK_INTERVAL);
    };

    waitForTarget();
  }

  // Builds a docs content selector from route data attributes — matches
  // the deepest level present (section > container > stage). Content items
  // are stamped with data-stage/data-container/data-section, so this stays
  // correct as CMS content changes.
  static page$GetDocsAttrSelector(routeData = {}) {
    const { stage, container, section } = routeData;

    if (stage === undefined || stage === "") {
      return undefined;
    }

    let sel = `.docs-item[data-stage='${stage}']`;

    // Examples children are hidden tab panels — scroll targets always
    // resolve to the stage element, which holds the visible code container.
    if (stage === "examples") {
      return sel;
    }

    if (container !== undefined && container !== "") {
      sel += `[data-container='${container}']`;
    }
    if (section !== undefined && section !== "") {
      sel += `[data-section='${section}']`;
    }

    return sel;
  }

  static page$GetPageSectionByRouteData(routeData, props = this.props) {
    function joinStrings(array) {
      // Filter out undefined values
      const filteredArray = array.filter((item) => item !== undefined);
      const joinedString = filteredArray.join("-");

      // Remove trailing dash if present
      const resultString = joinedString.endsWith("-")
          ? joinedString.slice(0, -1)
          : joinedString;

      // Join the array with "-"
      return resultString;
    }

    const { pageId, stage, container, section } = routeData;
    const addHash = props?.pageId === 'docs' ? '' : "#";

    return `${addHash}${joinStrings([pageId, stage, container, section])}`;
  }

  page$GetDocsSelectorByRouteVal(routeValue=""){

    const arr = [
      {
        "pageId": "docs",
        "stage": "intro",
        "url": "/docs/intro",
        "selector": "[data-pageid=\"docs\"][data-stage=\"intro\"]"
      },
      {
        "pageId": "docs",
        "stage": "intro",
        "container": "spynejs-system",
        "url": "/docs/intro/spynejs-system",
        "selector": ".docs-item.docs-item-container.docs-container-spyneIntro2"
      },
      {
        "pageId": "docs",
        "stage": "intro",
        "container": "core-concepts",
        "url": "/docs/intro/core-concepts",
        "selector": ".docs-item.docs-item-container.docs-container-spyneIntro3"
      },
      {
        "pageId": "docs",
        "stage": "spyneapp-init",
        "url": "/docs/spyneapp-init",
        "selector": ".docs-item-stage.docs-stage-spyneAppInitialize"
      },
      {
        "pageId": "docs",
        "stage": "spyneapp-init",
        "container": "event-channels",
        "url": "/docs/spyneapp-init/event-channels",
        "selector": ".docs-item-container.docs-container-eventChannels.heading-event-channels"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "url": "/docs/examples",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "container": "hello-world-vbl",
        "url": "/docs/examples/hello-world-vbl",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "container": "todos-app",
        "url": "/docs/examples/todos-app",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "container": "single-active-page",
        "url": "/docs/examples/single-active-page",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "container": "nesting-viewstreams",
        "url": "/docs/examples/nesting-viewstreams",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "container": "meme-generator",
        "url": "/docs/examples/meme-generator",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "examples",
        "container": "vehicle-360",
        "url": "/docs/examples/vehicle-360",
        "selector": "#docs-examples"
      },
      {
        "pageId": "docs",
        "stage": "migration",
        "url": "/docs/migration",
        "selector": ".docs-stage-migrationExammples"
      },
      {
        "pageId": "docs",
        "stage": "migration",
        "container": "migrating-from-angularjs",
        "url": "/docs/migration/migrating-from-angularjs",
        "selector": ".docs-container-migratingAngularJS"
      },
      {
        "pageId": "docs",
        "stage": "migration",
        "container": "migrating-from-react",
        "url": "/docs/migration/migrating-from-react",
        "selector": ".docs-container-migratingFromReact"
      }
    ]

    const clean = (s) => s.replace(/^\/+|\/+$/g, '');


    //console.log("route Value is ",{routeValue, arr})

    return arr.find(o=>clean(o.url) === clean(routeValue))?.selector;


  }

  static page$OnDeepLink(e) {
    const onWindowScrollTo = (el) => {
      // const { top, height } = el.getBoundingClientRect();
      //const pos = top - (height - 100);
      this.page$ScrollSectionIntoView(e);

     //console.log("DOCS WINDOW SCROLL ",{pos, top, e, el})
      /*    window.scrollTo(0, pos);

          gsap.to(window, {
            duration: .25,
            scrollTo: {y: el, offsetY: this.props.scrollOffsetY},
          });*/


    };

   //console.log("WINDOW DEEP LINK ",this.props.pageId, this.props, {e});

    const onDeepLinkNav = () => {
      const pageIdVal = this?.props?.pageId;

      // Docs and Reference content renders asynchronously (per-item rAF +
      // lazy hydration) and shifts while it grows — both route through the
      // wait-and-correct scroller with a data-driven selector. The
      // hardcoded URL map is only a fallback for legacy docs routes.
      if (pageIdVal === "docs" || pageIdVal === "reference") {
        const attrSel = PageTraits.page$GetDocsAttrSelector(
            e.payload.routeData,
        );
        const docsSel =
            attrSel ??
            this.page$GetDocsSelectorByRouteVal(e?.payload?.routeValue);

        if (docsSel !== undefined){
          this.page$ScrollDocsSelIntoView(docsSel);
        }
        return;
      }

      const idSel = PageTraits.page$GetPageSectionByRouteData(
          e.payload.routeData, this.props
      );

      const el$ = this?.props?.el$(idSel);
      if (el$?.exists) {
          el$.el.scrollIntoView();
      }
    };
    const onDeepLinkRelease = () => {
      // Settle-based resume: the spy re-enables once the deep-link scroll
      // (and any late reflow re-scrolls) has gone quiet, instead of a flat
      // flag flip that could land mid-animation. (Guarded — some pages use
      // this trait without composing ScrollSpyTraits.)
      if (typeof this.scrollSpy$PauseScrollSpy === "function") {
        this.scrollSpy$PauseScrollSpy();
      }
      onDeepLinkNav();
    };

    //   window.setTimeout(onDeepLinkNav, 700);
    //   window.setTimeout(onDeepLinkNav, 600);
    window.setTimeout(onDeepLinkRelease, 800);
  }


  static page$ScrollElementToTop3(scrollElSelector, yOffset = 0, animTime = 500) {
    return new Promise((resolve) => {
      const scrollEl = document.querySelector(scrollElSelector);

      // Check if element exists
      if (!scrollEl) {
        console.warn(`Element with selector "${scrollElSelector}" not found`);
        resolve(false);
        return;
      }

      // Variables to track scroll state
      let startTime = null;
      let startScrollTop = scrollEl.scrollTop;
      let targetScrollTop = yOffset;
      let lastScrollTop = scrollEl.scrollTop;
      let unchangedScrollCount = 0;
      let maxTries = 10; // Prevent infinite attempts
      let tryCount = 0;

      // Check if element can actually be scrolled (has enough content)
      const checkScrollability = () => {
        // If scrollHeight <= clientHeight, there's not enough content to scroll
        if (scrollEl.scrollHeight <= scrollEl.clientHeight) {
          console.info("Not enough content to scroll");
          resolve(false);
          return false;
        }
        return true;
      };

      // Initial scrollability check
      if (!checkScrollability()) return;

      // Use requestAnimationFrame for smooth animation
      const scrollStep = (timestamp) => {
        // Initialize start time on first frame
        if (!startTime) startTime = timestamp;

        // Calculate time elapsed
        const elapsed = timestamp - startTime;

        // Calculate progress (0 to 1)
        const progress = Math.min(elapsed / animTime, 1);

        // Apply easing function (ease-out cubic)
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        // Calculate current scroll position
        const currentScroll = startScrollTop - (startScrollTop - targetScrollTop) * easeProgress;

        // Apply scroll
        scrollEl.scrollTop = currentScroll;

        // Check if scroll position hasn't changed
        if (Math.abs(scrollEl.scrollTop - lastScrollTop) < 1) {
          unchangedScrollCount++;
        } else {
          unchangedScrollCount = 0;
        }
        lastScrollTop = scrollEl.scrollTop;

        // Determine if scrolling should continue
        if (progress < 1 && unchangedScrollCount < 5 && tryCount < maxTries) {
          // Continue animation
          requestAnimationFrame(scrollStep);
        } else {
          // Check if we're close enough to target or can't scroll further
          const isCloseEnough = Math.abs(scrollEl.scrollTop - targetScrollTop) < 5;
          const isStuck = unchangedScrollCount >= 5;

          if (isCloseEnough) {
            // Successfully scrolled to target position
            console.info("Scroll completed successfully");
            resolve(true);
          } else if (isStuck) {
            // Element is stuck, can't scroll further
            console.info("Scroll stopped: cannot scroll further");
            resolve(false);
          } else if (tryCount >= maxTries) {
            // Max retries reached, possibly due to dynamic content
            console.info("Scroll stopped: max retries reached");
            resolve(false);
          } else {
            // Content might still be rendering, try again
            tryCount++;
            startTime = null;
            startScrollTop = scrollEl.scrollTop;

            // Check scrollability again in case content size has changed
            if (checkScrollability()) {
              setTimeout(() => requestAnimationFrame(scrollStep), 100);
            }
          }
        }
      };

      // Start the animation
      requestAnimationFrame(scrollStep);
    });
  }

  static page$ScrollElementToTop(selector, offset = 0, animationTime = 750) {
    const target = document.querySelector(selector);
    if (!target) {
      console.warn(`scrollElementToTop: No element found for selector "${selector}"`);
      return;
    }

    const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
    );
    const viewportHeight = window.innerHeight;
    const maxScrollY = documentHeight - viewportHeight;

    const targetTopAbsolute = target.getBoundingClientRect().top + window.scrollY + offset;
    const targetScrollY = Math.min(targetTopAbsolute, maxScrollY);

    let startTime = null;

    function animateScroll(currentTime) {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;

      const startY = window.scrollY;
      const distance = targetScrollY - startY;
      const progress = Math.min(elapsed / animationTime, 1);

      window.scrollTo(0, startY + distance * easeInOutQuad(progress));

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      } else {
        verifyScroll();
      }
    }

    function verifyScroll() {
      const currentScrollY = window.scrollY;
      const currentTop = target.getBoundingClientRect().top;
      const documentScrolledToBottom = Math.abs(currentScrollY - maxScrollY) <= 2;

      const idealTop = offset; // How far from viewport top the element should ideally be

      const topMatch = Math.abs(currentTop - idealTop) <= 2;

      if (topMatch || documentScrolledToBottom) {
        // Success: we're either at ideal offset or bottom limit
        return;
      }

     //console.log('Adjusting scroll (element shifted after render)');
      requestAnimationFrame(() => PageTraits.page$ScrollElementToTop(selector, offset, animationTime));
    }

    function easeInOutQuad(t) {
      return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    requestAnimationFrame(animateScroll);
    //window.setTimeout(animateScroll, 500);
  }

  static page$ScrollSectionIntoView(e, scrollBehavior = "smooth", props=this.props) {
    const { routeData, pathsChanged } = e.payload;

    const { pageId, stage, section, navSelector, container, isMenuLink } = routeData;

   //console.log("SCROLL INTO VIEW ",{props,routeData});

    const isDocsPage = props.pageId === 'docs';
    const pathChangeCheckArrs = isDocsPage? ['stage', 'section', 'container'] : ['stage', 'section'];

    const stageHasChanged = pathsChanged.some(s => pathChangeCheckArrs.includes(s));


    const scrollToClassBool =
        stageHasChanged &&
        (String(isMenuLink) === "true" || scrollBehavior === "auto");
    //console.log('paths changed ',{scrollToClassBool, stageHasChanged, scrollBehavior})

/*    console.log("REF SCROLLING INTO VIEW", e, {
      stageHasChanged,
      scrollToClassBool,
      pathsChanged,
    });*/

    if (scrollToClassBool === true) {
      if (typeof this.scrollSpy$PauseScrollSpy === "function") {
        this.scrollSpy$PauseScrollSpy();
      }

      let idSel;

      if (navSelector){
        idSel = navSelector;
      } else {
        idSel = routeData?.itemId
            ? `#${routeData.itemId}`
            : PageTraits.page$GetPageSectionByRouteData(routeData);
      }

      // Docs AND Reference hydrate for seconds and sections shift as
      // content above them grows — route both through the wait-and-correct
      // helper with the data-driven selector. The idSel fallback is unsafe
      // for cross-page menu links: a link's data-item-id refers to an
      // element on the ORIGIN page (e.g. a docs card linking into
      // reference), so it resolves to nothing here and the page would
      // never scroll off the stale position.
      if (isDocsPage === true || props.pageId === "reference") {
        const docsSel = navSelector
            ? navSelector
            : (PageTraits.page$GetDocsAttrSelector(routeData) ?? idSel);
        this.page$ScrollDocsSelIntoView(docsSel);
        return;
      }

      const el$ = this?.props?.el$(idSel);

/*     console.log("ROUTE SECTION Doc2 ", el$.exist, {
        pageId,
        isMenuLink,
        idSel,
        stage,
        section,
        routeData,
        e,
        el$,
      });*/

      if (el$?.exists) {
        // el$.el.scrollIntoView({behavior: scrollBehavior});
        //gsap.to(idSel, { duration: 2, scrollTo: 250 });
        const isDocsExample = pageId === "docs" && container === "examples";

        if (isDocsExample === true) {
          //idSel = ".docs-container-examples";
        }

        const offsetYNum = this?.props?.scrollOffsetY || 0;

        const delayScroll = (dur=1)=> {
          const durNum = Number(dur) || 1;
          // Re-arm the pause on EVERY corrective scroll: the 1750ms retry
          // and waitForStableHeight can fire seconds after the original
          // pause resumed (docs content hydrates late), and an unpaused
          // corrective scroll lets the spy hijack the user's selection
          // mid-flight. The pause resumes on its own once scrolling has
          // settled — no onComplete re-enable.
          if (typeof this.scrollSpy$PauseScrollSpy === "function") {
            this.scrollSpy$PauseScrollSpy();
          }
          gsap.to(window, {
            duration: .75,
            scrollTo: {y: idSel, offsetY: offsetYNum},
          });

        }

        // const delayFast = () => delayScroll(.2);

/*        const checkSize = ()=>console.log(document.querySelector(idSel).offsetHeight);
        checkSize();
        requestAnimationFrame(checkSize);
        window.setTimeout(checkSize, 200);
        window.setTimeout(checkSize, 500);
        window.setTimeout(checkSize, 1000);
        window.setTimeout(checkSize, 1500);*/
        //window.setTimeout(delayScroll, 550);
        requestAnimationFrame(delayScroll);
        //  window.setTimeout(delayScroll, 50);
        window.setTimeout(delayScroll, 1750);


        function waitForStableHeight(selector, callback, checkInterval = 100, maxChecks = 50) {
          const target = document.querySelector(selector);
          if (!target) {
            console.warn(`waitForStableHeight: No element found for selector "${selector}"`);
            return;
          }

          let lastHeight = target.offsetHeight;
          let stableCount = 0;
          let checks = 0;

          const intervalId = setInterval(() => {
            const currentHeight = target.offsetHeight;

            if (currentHeight === lastHeight) {
              stableCount++;
            } else {
              stableCount = 0; // Reset counter if height changes
            }

            lastHeight = currentHeight;
            checks++;

            if (stableCount >= 2 || checks >= maxChecks) {
              clearInterval(intervalId);
              callback(target);
            }
          }, checkInterval);
        }

        waitForStableHeight(idSel, delayScroll);

        //this.page$ScrollElementToTop(idSel);
        //window.setTimeout(this.page$ScrollElementToTop.bind(this), 500);
       //console.log("id sel ", idSel, isDocsExample);

        //gsap.to(this.props.el, { duration: 2, scrollTo: { y: 500, offsetY: 50 } });
      }
    } else if (
        isDocsPage &&
        stage === "examples" &&
        pathsChanged.includes("container") &&
        String(container ?? "").length > 0
    ) {
      // The code container's forward/back arrows change the example via a
      // route send that is not a menu link — re-pin the Examples section
      // and hold it while the example's code animation resizes the
      // container. Requires a target container: spy sends entering
      // Examples carry container "" and must not yank a free scroll.
      // Browser history (no srcElement) lands instantly; arrows animate.
      this.page$ScrollDocsSelIntoView(
          ".docs-item[data-stage='examples']",
          this.props,
          { instant: e?.srcElement?.el === undefined },
      );
    } else if (
        props.pageId === "reference" &&
        e?.srcElement?.el === undefined &&
        String(routeData?.classMethod ?? "").length > 0 &&
        pathsChanged.some((s) => ["stage", "classMethod"].includes(s))
    ) {
      // Browser history within reference: method buttons and menu links
      // carry a srcElement, popstate routes don't — bring the class
      // section into view instantly so the restored example is visible.
      // Spy sends never carry classMethod, so they can't trigger this.
      this.page$ScrollDocsSelIntoView(
          `.docs-item[data-stage='${routeData.stage}']`,
          this.props,
          { instant: true },
      );
    } else if (
        e.payload?.pathsChanged[0] === "pageId" &&
        e.payload.routeData.pageId === this?.props?.pageId
    ) {
      this.page$OnDeepLink(e);
    }
  }

  static page$GetData(pageId) {
    const dataPath = ["Spyne", "config", "dynamicData", "content"];
    return compose(
        head,
        filter(propEq(pageId, "pageId")),
        path(dataPath),
    )(window);
  }
}
